<template>
	<footer>
		<div class="footer-body">
			<div class="footer-logo-container">
				<div class="mb-sm-8 mb-4">
					<img v-if="logoUrl" :height="logoHeight" :src="logoUrl" :alt="logoAltText" />
				</div>
				<div>
					<p class="text-white">
						{{ licenseText }}
					</p>
				</div>
			</div>

			<div class="store-logos">
				<h4 class="mb-sm-8 mb-4">{{ getGenericContent('footer-app-store-title') }}</h4>
				<div class="store-logos-container">
					<a
						:href="`${getGenericContent('footer-app-store-link-apple')}`"
						target="_blank"
						rel="noopener noreferrer"
					>
						<img
							class="mr-4"
							:src="AppleAppBadge"
							:alt="`${getGenericContent('footer-app-store-alt-apple')}`"
						/>
					</a>
					<a
						:href="`${getGenericContent('footer-app-store-link-google')}`"
						target="_blank"
						rel="noopener noreferrer"
					>
						<img
							class="mr-4"
							:src="GooglePlayBadge"
							:alt="`${getGenericContent('footer-app-store-alt-google')}`"
						/>
					</a>
				</div>
			</div>
		</div>

		<div class="footer-copyright">
			<p>{{ getGenericContent('footer-copy-label') }} {{ copyYear }} {{ copyrightText }}</p>
			<a @click="showDisclaimer = !showDisclaimer">
				{{ getGenericContent('footer-linking-button') }} <v-icon icon="mdi:mdi-open-in-new" />
			</a>
		</div>
	</footer>
	<LinkingDisclaimer v-model="showDisclaimer" @closeDialog="showDisclaimer = false" />
</template>

<script lang="ts" setup>
// ====---------------====
// 🚀 Imports
// ====---------------====
import { computed, onMounted, ref } from 'vue'
import { useSelectedPlanStore } from '@/stores/plans/selectedPlan.ts'
import { getGenericContent } from '@/services/cms/utils'
import LinkingDisclaimer from './LinkingDisclaimerModal.vue'
import AppleAppBadge from '@/assets/app-store-logo.svg'
import GooglePlayBadge from '@/assets/google-play-logo.svg'
import LogoAbcbs from '@/assets/abcbs-logo.svg'
import LogoHa from '@/assets/ha-logo.svg'
import LogoBa from '@/assets/ba-logo.svg'
import LogoOctave from '@/assets/octave-logo.svg'
import LogoSkai from '@/assets/logo-skai.svg'
import LogoAbm from '@/assets/abm-logo.svg'

// ====---------------====
// 🏪 Stores
// ====---------------====
const planStore = useSelectedPlanStore().getStore()

// ====---------------====
// 💡 Data
// ====---------------====
const showDisclaimer = ref<boolean>(false)
const licenseText = ref<string>('')
const logoUrl = ref<string>('')
const copyrightText = ref<string>('')
const logoAltText = ref<string>('')
const lobAbbr = ref<string>('')
const tier1 = ref<string>('')

const copyYear = computed<string>(() => {
	const now = new Date()
	return now.getFullYear().toString()
})

const logoHeight = computed<string | undefined>(() => {
	return tier1.value === 'skai' ? '48' : undefined
})

const logoMap: Record<string, string> = {
	abcbs: LogoAbcbs,
	ha: LogoHa,
	ba: LogoBa,
	octave: LogoOctave,
	skai: LogoSkai,
	abm: LogoAbm
}

// ====---------------====
// 🛠 Methods
// ====---------------====
function setupFooterData() {
	const lob = planStore.tier1.toLowerCase()

	licenseText.value = getGenericContent(`footer-license-text-${lob}`) as string
	logoUrl.value = logoMap[lob] || (logoMap['abcbs'] as string)
	copyrightText.value = getGenericContent(`footer-lob-full-${lob}`) as string
	logoAltText.value = getGenericContent(`footer-lob-full-${lob}`) as string
	lobAbbr.value = getGenericContent(`footer-lob-abbr-${lob}`) as string
	tier1.value = lob
}

// ====---------------====
// 🌄 Lifecycle
// ====---------------====
onMounted(() => {
	setupFooterData()
})
</script>

<style lang="scss" scoped>
.text-white {
	color: #fff;
}

footer {
	background-color: $primary-800;
	color: #fff;
	display: flex;
	justify-content: center;
	align-items: center;
	flex-direction: column;
}

.footer-body {
	display: flex;
	align-items: center;
	gap: 1.5rem;
	justify-content: space-between;
	max-width: 1440px;
	padding-top: 76px;
	padding-bottom: 46px;
	width: 100%;
	padding-inline: var(--page-padding-inline, 32px);
}

.footer-copyright {
	display: flex;
	justify-content: flex-start;
	flex-direction: column;
	align-items: center;
	gap: 1rem;

	width: 100%;
	max-width: 1440px;
	padding-block: 0;
	padding-inline: var(--page-padding-inline, 32px);
	margin-bottom: 36px;
	padding-top: 2rem;

	position: relative;

	* {
		color: $primary-300;
	}

	a {
		&:hover,
		&:focus,
		:active {
			color: $neutral-50;
		}
	}

	.mdi-open-in-new {
		font-size: 1.25rem;
		padding-inline-start: 0.35rem;
	}

	@media only screen and (min-width: 768px) {
		flex-direction: row;
	}
}

.footer-copyright::before {
	content: '';
	height: 1px;
	width: 100%;

	position: absolute;
	top: -1rem;
	right: 0;
	background-color: #005987;

	margin-bottom: 36px;
}

.store-logos {
	height: 100%;

	a {
		&:hover,
		&:focus {
			opacity: 0.8;
		}
	}
}

@media only screen and (max-width: 774px) {
	.store-logos-container {
		display: flex;
		flex-direction: column;
		align-items: end;
	}

	.store-logos-container img {
		margin-right: 0 !important;
		margin-bottom: 12px;
	}
}

@media screen and (max-width: $small-glass) {
	.footer-body {
		flex-direction: column;
		padding-block: 48px 16px;
		margin: 0;
	}

	.footer-logo-container {
		max-width: 100%;
		margin-bottom: 24px;

		img {
			max-width: 100%;
		}
	}

	.store-logos {
		width: 100%;
	}

	.store-logos-container {
		display: block;
	}

	.store-logos-container img {
		margin-right: 16px !important;
		margin-bottom: 0;
	}

	.footer-copyright {
		padding-block: 0;
	}
}

@media print {
	footer {
		padding-inline: 0;

		background-color: transparent;

		color: #000 !important;
	}

	.footer-body {
		padding-block-start: 2rem;
		padding-inline: 0;
	}

	.text-white {
		color: #000 !important;
	}

	.footer-body {
		img {
			filter: invert(100%);
		}

		p {
			max-width: 500px;
		}
	}

	.store-logos {
		display: none;
	}

	.footer-copyright {
		display: block;

		padding-block-start: 1rem;
		padding-inline: 0;

		text-align: left;

		&::before {
			background-color: #000;
		}

		> a {
			display: none;
		}

		* {
			color: #000 !important;
		}
	}
}
</style>
